import { useRef } from 'react';
import { cn } from '@/lib/utils/cn';
import { FileUp, Pencil, Database } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useBaseUploads } from '../../api';
import type { BaseFileMode } from '../../types';

export interface ContactsFormValues {
  baseFileMode: BaseFileMode;
  contactList: string;
  baseName: string;
  oldBaseId: string;
}

interface Props {
  values: ContactsFormValues;
  setValues: (patch: Partial<ContactsFormValues>) => void;
  baseFileRef?: React.RefObject<HTMLInputElement>;
  title?: string;
  description?: string;
}

const MODES: Array<{
  value: BaseFileMode;
  label: string;
  icon: typeof FileUp;
  hint: string;
}> = [
  {
    value: 'contactList',
    label: 'Paste numbers',
    icon: Pencil,
    hint: 'Paste up to 50,000 numbers, one per line.',
  },
  {
    value: 'newBaseFile',
    label: 'Upload file',
    icon: FileUp,
    hint: '.txt or .csv file with one number per line.',
  },
  {
    value: 'oldBaseFile',
    label: 'Existing list',
    icon: Database,
    hint: 'Re-use a contact list you uploaded previously.',
  },
];

export function StepContacts({
  values,
  setValues,
  baseFileRef,
  title = 'Contacts',
  description = 'Pick how you want to supply the numbers this campaign should dial.',
}: Props) {
  const bases = useBaseUploads();
  const localRef = useRef<HTMLInputElement>(null);
  const inputRef = baseFileRef ?? localRef;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {MODES.map((mode) => {
              const Icon = mode.icon;
              const active = values.baseFileMode === mode.value;

              return (
                <button
                  key={mode.value}
                  type="button"
                  onClick={() =>
                    setValues({
                      baseFileMode: mode.value,
                    })
                  }
                  className={cn(
                    'flex flex-col items-start gap-2 rounded-md border p-3 text-left transition-colors',
                    active
                      ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                      : 'border-border hover:border-primary/40',
                  )}
                >
                  <Icon
                    className={cn('h-5 w-5', active ? 'text-primary' : 'text-muted-foreground')}
                  />
                  <div>
                    <div className="text-sm font-medium">{mode.label}</div>
                    <div className="text-xs text-muted-foreground">{mode.hint}</div>
                  </div>
                </button>
              );
            })}
          </div>

          {values.baseFileMode === 'contactList' && (
            <div className="space-y-2">
              <Label htmlFor="contact-list">Numbers</Label>
              <Textarea
                id="contact-list"
                value={values.contactList}
                onChange={(event) =>
                  setValues({
                    contactList: event.target.value,
                  })
                }
                rows={10}
                placeholder={'10 digit mobile number\n10 digit mobile number\n…'}
                className="font-mono text-xs"
              />
            </div>
          )}

          {values.baseFileMode === 'newBaseFile' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="base-name">List name</Label>
                <Input
                  id="base-name"
                  value={values.baseName}
                  onChange={(event) =>
                    setValues({
                      baseName: event.target.value,
                    })
                  }
                  placeholder="Friendly name for this list"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="base-file">File</Label>
                <Input
                  id="base-file"
                  ref={inputRef}
                  type="file"
                  accept=".csv,.txt,text/csv,text/plain"
                />
              </div>
            </div>
          )}

          {values.baseFileMode === 'oldBaseFile' && (
            <div className="space-y-2">
              <Label htmlFor="old-base">Existing list</Label>
              <Select
                value={values.oldBaseId}
                onValueChange={(value) =>
                  setValues({
                    oldBaseId: value,
                  })
                }
              >
                <SelectTrigger id="old-base">
                  <SelectValue
                    placeholder={bases.isLoading ? 'Loading lists…' : 'Pick an existing list'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {(bases.data ?? []).map((base) => (
                    <SelectItem key={base.baseId} value={String(base.baseId)}>
                      {base.baseName}
                      {base.totalNumbers != null && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          ({base.totalNumbers.toLocaleString()} numbers)
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {bases.data?.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  You don&apos;t have any uploaded lists yet. Use Upload or Paste instead.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
